
import React from 'react';
import { EstateAssets, BprAsset, BprAssetType } from '../../../types';
import { Briefcase, Trash2, Plus, Info } from 'lucide-react';

interface Props {
    data: EstateAssets;
    onChange: (assets: EstateAssets) => void;
}

const ASSET_TYPE_OPTIONS: { value: BprAssetType; label: string; rate: 100 | 50; desc: string }[] = [
    {
        value: 'sole_business',
        label: 'Sole trader / whole business',
        rate: 100,
        desc: 'A business carried on as a sole trader, or the whole of a partnership interest.'
    },
    {
        value: 'partnership_interest',
        label: 'Partnership interest',
        rate: 100,
        desc: 'An interest in a trading partnership (not mainly investment-holding).'
    },
    {
        value: 'unquoted_shares',
        label: 'Unquoted company shares',
        rate: 100,
        desc: 'Shares in an unquoted company, including AIM-listed shares (IHTM18131).'
    },
    {
        value: 'quoted_controlling_shares',
        label: 'Quoted controlling shares',
        rate: 50,
        desc: 'Quoted shares giving control of more than 50% of the voting rights (IHTM18141).'
    },
    {
        value: 'land_buildings_machinery',
        label: 'Land, buildings or machinery',
        rate: 50,
        desc: 'Assets owned personally but used wholly or mainly in the deceased\'s own business or partnership (IHTM18141).'
    },
];

const rateFromType = (type: BprAssetType | null): 100 | 50 | null => {
    if (!type) return null;
    const opt = ASSET_TYPE_OPTIONS.find(o => o.value === type);
    return opt?.rate ?? null;
};

export const BprStep: React.FC<Props> = ({ data, onChange }) => {
    const items = data.bpr_assets || [];

    const updateItem = (index: number, changes: Partial<BprAsset>) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], ...changes };
        onChange({ ...data, bpr_assets: newItems });
    };

    const addItem = () => {
        const newItem: BprAsset = {
            id: `bpr_${Date.now()}`,
            label: '',
            asset_type: null,
            estimated_value: null,
            relief_rate: null,
            years_owned: null,
            qualifies: null,
        };
        onChange({ ...data, bpr_assets: [...items, newItem] });
    };

    const removeItem = (index: number) => {
        onChange({ ...data, bpr_assets: items.filter((_, i) => i !== index) });
    };

    React.useEffect(() => {
        if (items.length === 0) addItem();
    }, []);

    return (
        <div className="max-w-2xl mx-auto space-y-8">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-ink">Business Property Relief</h2>
                <p className="text-ink3 mt-2">
                    Qualifying business assets can receive 50% or 100% relief from Inheritance Tax (IHTM18000).
                </p>
            </div>

            {/* Guidance banner */}
            <div className="bg-accent-soft border border-accent rounded-xl p-4 flex gap-3">
                <Info className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <div className="text-xs text-accent space-y-1">
                    <p className="font-bold">What qualifies for BPR?</p>
                    <p>The business must be a <strong>trading business</strong> — not one mainly dealing in investments, land, or securities. The deceased must have owned the asset for <strong>at least 2 years</strong> before death (IHTM18201).</p>
                    <p className="text-accent italic">This tool does not give legal or tax advice. Confirm eligibility with a solicitor or chartered tax adviser.</p>
                </div>
            </div>

            <div className="space-y-4">
                {items.map((item, idx) => {
                    const selectedOpt = ASSET_TYPE_OPTIONS.find(o => o.value === item.asset_type);
                    const ownsTooFew = item.years_owned !== null && item.years_owned < 2;
                    const reliefAmount = (item.qualifies === true && item.years_owned !== null && item.years_owned >= 2 && item.estimated_value && item.relief_rate)
                        ? item.estimated_value * (item.relief_rate / 100)
                        : null;

                    return (
                        <div key={item.id} className="bg-white p-5 rounded-xl border border-rule relative">
                            {items.length > 1 && (
                                <button
                                    onClick={() => removeItem(idx)}
                                    className="absolute top-4 right-4 text-sub hover:text-ink2 transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}

                            <div className="flex flex-col gap-4">
                                {/* Description */}
                                <div>
                                    <label className="block text-xs font-bold text-ink3 uppercase mb-1">Description</label>
                                    <div className="flex items-center gap-2">
                                        <Briefcase className="w-5 h-5 text-ink3" />
                                        <input
                                            type="text"
                                            placeholder='e.g. "Smith & Sons Ltd shares"'
                                            className="w-full border-b border-rule focus:border-accent focus:outline-none py-1 text-ink font-medium"
                                            value={item.label || ''}
                                            onChange={(e) => updateItem(idx, { label: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Asset Type */}
                                <div>
                                    <label className="block text-xs font-bold text-ink3 uppercase mb-2">Asset Type</label>
                                    <div className="space-y-2">
                                        {ASSET_TYPE_OPTIONS.map(opt => (
                                            <label
                                                key={opt.value}
                                                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                                                    ${item.asset_type === opt.value
                                                        ? 'border-accent bg-accent-soft'
                                                        : 'border-rule bg-white hover:border-rule'}`}
                                            >
                                                <input
                                                    type="radio"
                                                    className="mt-0.5 text-accent"
                                                    name={`bpr_type_${item.id}`}
                                                    checked={item.asset_type === opt.value}
                                                    onChange={() => updateItem(idx, {
                                                        asset_type: opt.value,
                                                        relief_rate: opt.rate
                                                    })}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-bold text-ink2">{opt.label}</span>
                                                        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded uppercase ${opt.rate === 100 ? 'bg-paper text-ink2' : 'bg-[#F5E8C4] text-[#7A5A00]'}`}>
                                                            {opt.rate}% relief
                                                        </span>
                                                    </div>
                                                    <p className="text-[11px] text-ink3 mt-0.5">{opt.desc}</p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {item.asset_type && (
                                    <>
                                        {/* Value */}
                                        <div className="flex gap-4 flex-wrap">
                                            <div className="flex-1 min-w-[140px]">
                                                <label className="block text-xs font-bold text-ink3 uppercase mb-1">Estimated Value (£)</label>
                                                <input
                                                    type="number"
                                                    className="w-full border rounded px-3 py-2 text-sm focus:ring-1 focus:ring-accent focus:outline-none"
                                                    placeholder="0"
                                                    value={item.estimated_value ?? ''}
                                                    onChange={(e) => updateItem(idx, { estimated_value: e.target.value ? Number(e.target.value) : null })}
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[140px]">
                                                <label className="block text-xs font-bold text-ink3 uppercase mb-1">Years Owned</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className={`w-full border rounded px-3 py-2 text-sm focus:ring-1 focus:outline-none ${ownsTooFew ? 'border-rule focus:ring-rule' : 'focus:ring-accent'}`}
                                                    placeholder="e.g. 5"
                                                    value={item.years_owned ?? ''}
                                                    onChange={(e) => updateItem(idx, { years_owned: e.target.value ? Number(e.target.value) : null })}
                                                />
                                                {ownsTooFew && (
                                                    <p className="text-[10px] text-ink2 mt-1">BPR requires at least 2 years ownership (IHTM18201).</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Qualifying question */}
                                        <div>
                                            <label className="block text-xs font-bold text-ink3 uppercase mb-2">
                                                Does this business mainly trade (not mainly hold investments)?
                                            </label>
                                            <div className="flex gap-3">
                                                {[
                                                    { val: true, label: 'Yes — it trades' },
                                                    { val: false, label: 'No / Not sure' }
                                                ].map(opt => (
                                                    <label key={String(opt.val)} className={`
                                                        flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer text-sm font-medium transition-colors
                                                        ${item.qualifies === opt.val
                                                            ? opt.val ? 'bg-paper border-rule text-ink' : 'bg-paper border-rule text-ink2'
                                                            : 'bg-white border-rule text-ink3 hover:bg-paper'}
                                                    `}>
                                                        <input
                                                            type="radio"
                                                            className="sr-only"
                                                            name={`bpr_qualifies_${item.id}`}
                                                            checked={item.qualifies === opt.val}
                                                            onChange={() => updateItem(idx, { qualifies: opt.val })}
                                                        />
                                                        {opt.label}
                                                    </label>
                                                ))}
                                            </div>
                                            {item.qualifies === false && (
                                                <p className="text-[11px] text-[#7A5A00] mt-2 bg-[#FAF3E0] p-2 rounded border border-[#E8D89A]">
                                                    Investment-holding businesses generally do not qualify for BPR. The full value will be included in the taxable estate.
                                                </p>
                                            )}
                                        </div>

                                        {/* Relief summary */}
                                        {reliefAmount !== null && (
                                            <div className="bg-paper border border-rule rounded-lg p-3 flex justify-between items-center">
                                                <span className="text-sm font-bold text-ink">
                                                    {item.relief_rate}% BPR Relief Applied
                                                </span>
                                                <span className="text-sm font-mono font-black text-ink2">
                                                    −{reliefAmount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                                                </span>
                                            </div>
                                        )}
                                        {item.qualifies === true && ownsTooFew && (
                                            <div className="bg-paper border border-rule rounded-lg p-3">
                                                <p className="text-sm font-bold text-ink2">Relief not applied — owned less than 2 years.</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}

                <button
                    onClick={addItem}
                    className="w-full py-3 border-2 border-dashed border-rule rounded-lg text-ink3 font-bold hover:border-accent hover:text-accent transition-all flex items-center justify-center gap-2 text-sm"
                >
                    <Plus className="w-4 h-4" /> Add Another Business Asset
                </button>
            </div>
        </div>
    );
};
